import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { CREATE_CANDIDATES } from "../../utils/constants";
import useRequireAuth from "../../utils/useRequireAuth";
import { useNavigate } from "react-router-dom";
import LayoutHOC from "../LayoutHOC";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useCompany from "../../hooks/useCompany";

const CreateCandidate = () => {
    const user = useRequireAuth();
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState(null);

    const company = useCompany();

    const initialValues = {
      name: "",
      email: "",
      mobile: "",
      date_of_birth: "",
      password: "",
      experience: "",
      jobPosiiton: "",
      education: "",
      skills: [],
      languages: [],
      profilePicture: null,
      resume: null,
      maritalStatus: "",
      gender: "",
      address: "",
      about: "",
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Name is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        mobile: Yup.string().required("Mobile number is required"),
        date_of_birth: Yup.date().required("Date of birth is required"),
        password: Yup.string()
            .min(8, "Password must be at least 8 characters")
            .required("Password is required"),
    });

    const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
        try {
            const Authtoken = user.token;
            const formDataWithFile = new FormData();

            let dateOfBirth = values.date_of_birth;
            if (dateOfBirth instanceof Date) {
              const day = dateOfBirth.getDate().toString().padStart(2, "0");
              const month = (dateOfBirth.getMonth() + 1).toString().padStart(2, "0");
              const year = dateOfBirth.getFullYear();
              dateOfBirth = `${year}-${month}-${day}`;
            }

           formDataWithFile.append("name", values.name);
           formDataWithFile.append("email", values.email);
           formDataWithFile.append("mobile", values.mobile);
           formDataWithFile.append("date_of_birth", dateOfBirth);
           formDataWithFile.append("password", values.password);
           formDataWithFile.append("experienced", values.experience || "");
           formDataWithFile.append("professionid", values.jobPosiiton || "");
           formDataWithFile.append("education", values.education || "");
           formDataWithFile.append("skills", Array.isArray(values.skills) ? JSON.stringify(values.skills) : values.skills);
           formDataWithFile.append("languages", Array.isArray(values.languages) ? JSON.stringify(values.languages) : values.languages);
           if (values.profilePicture) {
             formDataWithFile.append("profilePicture", values.profilePicture);
           }
           if (values.resume) {
             formDataWithFile.append("resume", values.resume);
           }
           formDataWithFile.append("maritalStatus", values.maritalStatus || "");
           formDataWithFile.append("gender", values.gender || "");
           formDataWithFile.append("address", values.address || "");
           formDataWithFile.append("about", values.about || "");

            const response = await fetch(CREATE_CANDIDATES, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${Authtoken}`,
                },
                body: formDataWithFile,
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            navigate(`/candidate/`);
            setSubmitting(false);
        } catch (error) {
            setFieldError("form", "An error occurred while submitting the form.");
            setSubmitting(false);
        }
    };

    return (
      <main className="p-4">
        <div className="p-4 mt-14">
          <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 font-poppins">
            <div className="bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-700 rounded-xl shadow-xl mb-6 overflow-hidden">
              <div className="px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Link to="/candidate" className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                    </Link>
                    <div>
                      <h1 className="text-2xl font-bold text-white">Create New Candidate</h1>
                      <p className="text-teal-100 text-sm mt-1">Add a new candidate to the system</p>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-white font-medium">Candidate Registration</span>
                  </div>
                </div>
              </div>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, setFieldValue }) => (
                <Form id="userProfile" encType="multipart/form-data">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
                    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 px-6 py-4 border-b border-teal-100">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-teal-100 rounded-lg">
                          <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Personal Information</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <Field name="name">
                          {({ field }) => (
                            <div>
                              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                Full Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                {...field}
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                                type="text"
                                placeholder="Enter full name"
                              />
                              <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="email">
                          {({ field }) => (
                            <div>
                              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                Email Address <span className="text-red-500">*</span>
                              </label>
                              <input
                                {...field}
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                                type="email"
                                placeholder="Enter email address"
                              />
                              <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="mobile">
                          {({ field }) => (
                            <div>
                              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">
                                Mobile Number <span className="text-red-500">*</span>
                              </label>
                              <input
                                {...field}
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                                type="text"
                                placeholder="Enter mobile number"
                              />
                              <ErrorMessage name="mobile" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="date_of_birth">
                          {({ field }) => (
                            <div>
                              <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-2">
                                Date of Birth <span className="text-red-500">*</span>
                              </label>
                              <DatePicker
                                selected={selectedDate}
                                onChange={(date) => {
                                  setSelectedDate(date);
                                  setFieldValue("date_of_birth", date);
                                }}
                                dateFormat="yyyy-MM-dd"
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                                placeholderText="Select date of birth"
                              />
                              <ErrorMessage name="date_of_birth" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="password">
                          {({ field }) => (
                            <div>
                              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                Password <span className="text-red-500">*</span>
                              </label>
                              <input
                                {...field}
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                                type="password"
                                placeholder="Enter password"
                              />
                              <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="gender">
                          {({ field }) => (
                            <div>
                              <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                                Gender
                              </label>
                              <select
                                {...field}
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                              >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                              </select>
                              <ErrorMessage name="gender" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="maritalStatus">
                          {({ field }) => (
                            <div>
                              <label htmlFor="maritalStatus" className="block text-sm font-medium text-gray-700 mb-2">
                                Marital Status
                              </label>
                              <select
                                {...field}
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                              >
                                <option value="">Select Status</option>
                                <option value="Single">Single</option>
                                <option value="Married">Married</option>
                                <option value="Divorced">Divorced</option>
                                <option value="Widowed">Widowed</option>
                              </select>
                              <ErrorMessage name="maritalStatus" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                       <Field name="address">
                          {({ field }) => (
                            <div className="lg:col-span-2">
                              <label
                                htmlFor="address"
                                className="block text-sm font-medium text-gray-700 mb-2"
                              >
                                Address
                              </label>

                              <input
                                {...field}
                                readOnly
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed focus:outline-none"
                                type="text"
                                placeholder="Synced with Aadhaar Card"
                              />

                              <ErrorMessage
                                name="address"
                                component="div"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>
                          )}
                        </Field>

                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
                    <div className="bg-gradient-to-r from-cyan-50 to-teal-50 px-6 py-4 border-b border-cyan-100">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-cyan-100 rounded-lg">
                          <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Professional Details</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <Field name="experience">
                          {({ field }) => (
                            <div>
                              <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                                Experience
                              </label>
                              <Field
                                as="select"
                                id="experience"
                                name="experience"
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                              >
                                <option value="">Select Experience</option>
                                {company?.data?.experience?.map((experience) => (
                                  <option key={experience.id} value={experience.id}>
                                    {experience.name}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage name="experience" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="jobPosiiton">
                          {({ field }) => (
                            <div>
                              <label htmlFor="jobPosiiton" className="block text-sm font-medium text-gray-700 mb-2">
                                Job Position
                              </label>
                              <Field
                                as="select"
                                id="jobPosiiton"
                                name="jobPosiiton"
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                              >
                                <option value="">Select Job Position</option>
                                {company?.data?.job_position?.map((job_position) => (
                                  <option key={job_position.id} value={job_position.id}>
                                    {job_position.name}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage name="jobPosiiton" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="education">
                          {({ field }) => (
                            <div>
                              <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-2">
                                Education
                              </label>
                              <Field
                                as="select"
                                id="education"
                                name="education"
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                              >
                                <option value="">Select Education</option>
                                {company?.data?.education?.map((education) => (
                                  <option key={education.id} value={education.id}>
                                    {education.name}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage name="education" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="skills">
                          {({ field, form }) => (
                            <div>
                              <label htmlFor="skills" className="block text-sm font-medium text-gray-700 mb-2">
                                Skills
                              </label>
                              <select
                                {...field}
                                id="skills"
                                name="skills"
                                multiple
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 h-32"
                                value={field.value || []}
                                onChange={(event) => {
                                  const options = event.target.options;
                                  const value = [];
                                  for (let i = 0, l = options.length; i < l; i++) {
                                    if (options[i].selected) {
                                      value.push(options[i].value);
                                    }
                                  }
                                  form.setFieldValue(field.name, value);
                                }}
                              >
                                {company?.data?.skills?.map((skills) => (
                                  <option key={skills.id} value={skills.id}>
                                    {skills.name}
                                  </option>
                                ))}
                              </select>
                              <p className="text-gray-500 text-xs mt-1">Hold Ctrl/Cmd to select multiple</p>
                              <ErrorMessage name="skills" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="languages">
                          {({ field, form }) => (
                            <div>
                              <label htmlFor="languages" className="block text-sm font-medium text-gray-700 mb-2">
                                Languages
                              </label>
                              <select
                                {...field}
                                id="languages"
                                name="languages"
                                multiple
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 h-32"
                                value={field.value || []}
                                onChange={(event) => {
                                  const options = event.target.options;
                                  const value = [];
                                  for (let i = 0, l = options.length; i < l; i++) {
                                    if (options[i].selected) {
                                      value.push(options[i].value);
                                    }
                                  }
                                  form.setFieldValue(field.name, value);
                                }}
                              >
                                {company?.data?.languages?.map((language) => (
                                  <option key={language.id} value={language.id}>
                                    {language.name}
                                  </option>
                                ))}
                              </select>
                              <p className="text-gray-500 text-xs mt-1">Hold Ctrl/Cmd to select multiple</p>
                              <ErrorMessage name="languages" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="about">
                          {({ field }) => (
                            <div className="lg:col-span-3">
                              <label htmlFor="about" className="block text-sm font-medium text-gray-700 mb-2">
                                About
                              </label>
                              <textarea
                                {...field}
                                rows="4"
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                                placeholder="Write a brief description about the candidate..."
                              />
                              <ErrorMessage name="about" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 border-b border-emerald-100">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Documents & Uploads</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Field name="profilePicture">
                          {({ form }) => (
                            <div>
                              <label htmlFor="profilePicture" className="block text-sm font-medium text-gray-700 mb-2">
                                Profile Picture
                              </label>
                              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-teal-400 transition-colors">
                                <input
                                  id="profilePicture"
                                  name="profilePicture"
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(event) => {
                                    form.setFieldValue("profilePicture", event.currentTarget.files[0]);
                                  }}
                                />
                                <label htmlFor="profilePicture" className="cursor-pointer">
                                  <svg className="w-10 h-10 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <p className="text-sm text-gray-500">Click to upload profile picture</p>
                                  <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                                </label>
                              </div>
                              <ErrorMessage name="profilePicture" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>

                        <Field name="resume">
                          {({ form }) => (
                            <div>
                              <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-2">
                                Resume/CV
                              </label>
                              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-teal-400 transition-colors">
                                <input
                                  id="resume"
                                  name="resume"
                                  type="file"
                                  accept=".pdf,.doc,.docx"
                                  className="hidden"
                                  onChange={(event) => {
                                    form.setFieldValue("resume", event.currentTarget.files[0]);
                                  }}
                                />
                                <label htmlFor="resume" className="cursor-pointer">
                                  <svg className="w-10 h-10 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  <p className="text-sm text-gray-500">Click to upload resume</p>
                                  <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX up to 10MB</p>
                                </label>
                              </div>
                              <ErrorMessage name="resume" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          )}
                        </Field>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                    <Link to="/candidate">
                      <button
                        type="button"
                        className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Cancel
                      </button>
                    </Link>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-8 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-medium rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Create Candidate
                        </>
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </main>
    );
};

export default LayoutHOC(CreateCandidate);
